import React from "react";

import { trDts } from "@modules/datasheets/utils/i18n";

import { useMessage } from "@common/hooks/message";
import { HttpErrorDto } from "@common/dto/http";
import { DatasheetSettingsConfigureTableFormat, DatasheetSettingsConfigureTableProps } from "./configure-table.types";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";

export const useController = (props: DatasheetSettingsConfigureTableProps) => {
  const { onSubmit, tableFormats } = props;
  const { closeMessage, showErrorMessage, messageProps } = useMessage();

  const [internalTableFormats, setInternalTableFormats] = React.useState<Array<DatasheetSettingsConfigureTableFormat>>(
    []
  );

  const [formatNameOpened, setFormatNameOpened] = React.useState(false);

  const openFormatName = React.useCallback(() => {
    setFormatNameOpened(true);
  }, []);

  const closeFormatName = React.useCallback(() => {
    setFormatNameOpened(false);
  }, []);

  React.useEffect(() => {
    setInternalTableFormats(tableFormats);
  }, [tableFormats]);

  const [selectedFormatId, setSelectedFormatId] = React.useState<
    DatasheetSettingsConfigureTableFormat["id"] | undefined
  >();

  const [configureTableSubmitting, setConfigureTableSubmitting] = React.useState(false);

  const isAllowedToSubmit = !configureTableSubmitting;

  const handleSubmit = async (formatName: string) => {
    try {
      setConfigureTableSubmitting(true);
      await onSubmit({
        selectedDropdownFormatId: selectedFormatId,
        formats: internalTableFormats,
        formatName
      });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to get update configure table"));
    } finally {
      setConfigureTableSubmitting(false);
    }
  };

  const handleFormatOrderChange = React.useCallback((id: number | string, e: React.ChangeEvent<HTMLInputElement>) => {
    const order = Math.floor(Number(e.target.value));
    const defaultOrder = (Math.max(...internalTableFormats.map(i => Number(i.order))) || 0)  + 1;
    const changedOrder = Number.isNaN(order) ? defaultOrder : order;

    setInternalTableFormats((prev) =>
      prev.map((format) => (format.id == id ? { ...format, order: changedOrder } : format))
    );
  }, [internalTableFormats]);

  const formatTablesHeader: Array<{
    title: string;
    style: React.CSSProperties;
    component: (params: { item: DatasheetSettingsConfigureTableFormat }) => React.ReactNode;
  }> = React.useMemo(
    () => [
      {
        title: trDts("Order"),
        style: { width: "70px", fontWeight: 600, textAlign: "center" },
        component: ({ item }) => (
          <FormControl
            style={{ width: "70px" }}
            value={item.order}
            type="number"
            onChange={(e) => handleFormatOrderChange(item.id, e)}
            min={0}
          />
        ),
      },
      {
        title: trDts("Field Type"),
        style: { width: "70px", fontWeight: 600, textAlign: "center" },
        component: ({ item }) => (
          <Flex width="80px" alignItems="center" justify="center">
            <img style={{ height: "16px" }} src={item.imageURL} />
          </Flex>
        ),
      },
      {
        title: trDts("Field Name"),
        style: { fontWeight: 600, textAlign: "center", marginLeft: "10px" },
        component: ({ item }) => <Flex alignItems="center">{item.name}</Flex>,
      },
    ],
    [handleFormatOrderChange]
  );

  return {
    isAllowedToSubmit,
    handleSubmit,
    configureTableSubmitting,
    formatTablesHeader,
    setSelectedFormatId,
    selectedFormatId,
    closeMessage,
    messageProps,
    internalTableFormats,
    formatNameOpened,
    openFormatName,
    closeFormatName,
  };
};
